import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import {
  fmtCurrencyFull,
  fmtHours,
  fmtPct,
  STATUS,
  utilizationFillClass,
} from "@/lib/calc";
import StatusPill from "@/components/StatusPill";
import ActivityTimeline from "@/components/ActivityTimeline";
import RevenueSplit from "@/components/RevenueSplit";
import { useLayout } from "@/components/AppLayout";
import { ArrowLeft, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const Row = ({ label, value, accent }) => (
  <div className="flex items-center justify-between py-3 border-b border-[#EDE7D6] last:border-0">
    <div className="text-sm text-[#69747C]">{label}</div>
    <div className={`text-sm font-medium tabular-nums ${accent || "text-[#1F2326]"}`}>
      {value}
    </div>
  </div>
);

const ClientDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [settings, setSettings] = useState({ currency: "USD" });
  const [loading, setLoading] = useState(true);
  const [confirmDel, setConfirmDel] = useState(false);
  const { openAddClient, refreshKey } = useLayout();

  const load = async () => {
    setLoading(true);
    try {
      const [c, s] = await Promise.all([
        api.get(`/clients/${id}`),
        api.get("/settings"),
      ]);
      setClient(c.data);
      setSettings(s.data);
    } catch {
      toast.error("Client not found");
      navigate("/retainers", { replace: true });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, refreshKey]);

  const handleDelete = async () => {
    try {
      await api.delete(`/clients/${id}`);
      toast.success("Retainer deleted");
      navigate("/retainers", { replace: true });
    } catch {
      toast.error("Failed to delete");
    }
  };

  if (loading || !client) {
    return <div className="text-sm text-[#69747C]">Loading.</div>;
  }

  const currency = settings?.currency || "USD";
  const fillCls = utilizationFillClass(client.utilization_pct);
  const explanation = STATUS[client.status]?.description || "";
  const longExplanation = {
    on_track:
      "This client is within hours and margin target. Keep doing what you are doing.",
    close_to_limit:
      "This client is near the included hours, or margin is low. Watch scope this month.",
    losing_money:
      "This client has gone over hours, or costs too much to service. Time for a scope chat or a rate change.",
  }[client.status];

  return (
    <div className="space-y-6" data-testid="client-detail-page">
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate("/retainers")}
          data-testid="back-btn"
          className="inline-flex items-center gap-1.5 text-sm text-[#69747C] hover:text-[#1F2326] transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> All retainers
        </button>
        <div className="flex items-center gap-2">
          <button
            onClick={() => openAddClient(client)}
            data-testid="edit-client-btn"
            className="bg-white text-[#1F2326] border border-[#E4DFD2] hover:bg-[#FBFAF6] px-3 py-2 rounded-lg text-sm font-medium inline-flex items-center gap-1.5"
          >
            <Pencil className="w-3.5 h-3.5" /> Edit
          </button>
          <button
            onClick={() => setConfirmDel(true)}
            data-testid="delete-client-btn"
            className="bg-white text-[#7E332D] border border-[#E4DFD2] hover:bg-[#FAEEED] hover:border-[#E7BFBA] px-3 py-2 rounded-lg text-sm font-medium inline-flex items-center gap-1.5"
          >
            <Trash2 className="w-3.5 h-3.5" /> Delete
          </button>
        </div>
      </div>

      {/* Hero */}
      <div className="bg-white border border-[#E4DFD2] rounded-xl p-8">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="text-[11px] tracking-[0.08em] uppercase font-semibold text-[#69747C] flex items-center gap-2">
              <span>Retainer client</span>
              {client.service_type ? (
                <span className="bg-[#F0EADA] text-[#7A5E1F] px-2 py-0.5 rounded-full text-[10px] normal-case tracking-normal font-medium">
                  {client.service_type}
                </span>
              ) : null}
              {client.owner ? (
                <span className="text-[#69747C] normal-case tracking-normal font-normal">
                  · Owner: {client.owner}
                </span>
              ) : null}
            </div>
            <h2 className="font-display text-3xl font-semibold tracking-tight text-[#1F2326] mt-1">
              {client.name}
            </h2>
            {client.notes ? (
              <p className="text-sm text-[#69747C] mt-2 max-w-2xl">{client.notes}</p>
            ) : null}
          </div>
          <StatusPill status={client.status} size="lg" />
        </div>

        {/* Hours progress */}
        <div className="mt-7">
          <div className="flex items-baseline justify-between mb-2">
            <div className="text-xs text-[#69747C]">Hours used</div>
            <div className="text-xs text-[#69747C] tabular-nums">
              {fmtHours(client.logged_hours)} / {fmtHours(client.included_hours)} ({fmtPct(client.utilization_pct)})
            </div>
          </div>
          <div className="w-full bg-[#F0EADA] rounded-full h-2.5 overflow-hidden">
            <div
              className={`h-2.5 ${fillCls} transition-all duration-500`}
              style={{ width: `${Math.min(client.utilization_pct, 100)}%` }}
            />
          </div>
          {client.overage_hours > 0 ? (
            <div className="text-xs text-[#7E332D] mt-2 font-medium">
              Over by {fmtHours(client.overage_hours)}
            </div>
          ) : (
            <div className="text-xs text-[#69747C] mt-2">
              {fmtHours(client.remaining_hours)} hours left this month
            </div>
          )}
        </div>

        {/* Revenue split */}
        <div className="mt-7 pt-6 border-t border-[#EDE7D6]">
          <RevenueSplit client={client} currency={currency} />
        </div>

        {/* Status explanation */}
        <div
          className={`mt-6 rounded-lg border px-4 py-3 text-sm ${STATUS[client.status].bg} ${STATUS[client.status].border} ${STATUS[client.status].text}`}
        >
          <div className="font-semibold">
            {STATUS[client.status].label}. {explanation}
          </div>
          <div className="text-[13px] mt-1 opacity-80">{longExplanation}</div>
        </div>
      </div>

      {/* Two columns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-[#E4DFD2] rounded-xl p-6">
          <div className="text-[11px] tracking-[0.08em] uppercase font-semibold text-[#69747C] mb-2">
            Retainer
          </div>
          <Row
            label="Monthly retainer fee"
            value={fmtCurrencyFull(client.monthly_fee, currency)}
          />
          <Row
            label="Included monthly hours"
            value={fmtHours(client.included_hours)}
          />
          <Row
            label="Internal hourly cost"
            value={fmtCurrencyFull(client.hourly_cost, currency) + "/h"}
          />
          <Row
            label="Logged hours this month"
            value={fmtHours(client.logged_hours)}
          />
          <Row
            label="Hours left"
            value={fmtHours(client.remaining_hours)}
          />
          <Row
            label="Over by"
            value={fmtHours(client.overage_hours)}
            accent={client.overage_hours > 0 ? "text-[#7E332D]" : "text-[#1F2326]"}
          />
        </div>

        <div className="bg-white border border-[#E4DFD2] rounded-xl p-6">
          <div className="text-[11px] tracking-[0.08em] uppercase font-semibold text-[#69747C] mb-2">
            Profit
          </div>
          <Row
            label="Effective revenue per hour"
            value={
              client.logged_hours > 0
                ? fmtCurrencyFull(client.effective_hourly_revenue, currency) + "/h"
                : "—"
            }
          />
          <Row
            label="Costs to service"
            value={fmtCurrencyFull(client.estimated_cost, currency)}
          />
          <Row
            label="Profit"
            value={fmtCurrencyFull(client.estimated_profit, currency)}
            accent={
              client.estimated_profit >= 0 ? "text-[#42624B]" : "text-[#7E332D]"
            }
          />
          <Row
            label="Margin"
            value={fmtPct(client.profit_margin)}
            accent={
              client.profit_margin >= 35
                ? "text-[#42624B]"
                : client.profit_margin >= 15
                ? "text-[#7A5E1F]"
                : "text-[#7E332D]"
            }
          />
          <Row label="Utilization" value={fmtPct(client.utilization_pct)} />
        </div>
      </div>

      {/* Activity timeline */}
      <ActivityTimeline clientId={client.id} onChange={load} />

      <AlertDialog open={confirmDel} onOpenChange={setConfirmDel}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {client.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the retainer from your dashboard. This can not be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-btn"
              onClick={handleDelete}
              className="bg-[#C76055] text-white hover:bg-[#A04A40]"
            >
              Delete retainer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ClientDetail;
